import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getLocations,
  createLocation,
  updateLocation,
  deleteLocation,
} from '../api/locationAPI';
import { getHierarchyStructures } from '../api/hierarchy-structureAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useValidator } from '../utils/useValidator';
import { enhanceHierarchyOptions } from '../utils/hierarchy';
import { formatDepartmentOption } from '../utils/formatDepartmentOption';

const WarehouseListLayer = () => {
  const userProfile = useUserProfile();
  const [warehouseList, setWarehouseList] = useState([]);
  const [newWarehouse, setNewWarehouse] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState(true);

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchWarehouses = async () => {
    try {
      const data = await getLocations();
      setWarehouseList(data);
    } catch (err) {
      console.error('Error fetching warehouses:', err);
    }
  };


  const inputFields = [
    { field: 'name', label: 'Name', type: 'text', size: '6', required: true },
    { field: 'description', label: 'Description', type: 'text', size: '6' },
    {
      field: 'departments',
      label: 'Departments',
      type: 'multi-select',
      size: '6',
      options: enhanceHierarchyOptions(
        departments.map((d) => ({ value: d.id, label: d.name, parentId: d.parentId })),
      ).options,
      formatOptionLabel: formatDepartmentOption,
      disabled: allDepartments,
    },
    { field: 'allDepartments', label: 'All Departments', type: 'switch', size: '6' },
  ];

  const {
    errors,
    setErrors,
    validate,
    validateDepartmentsSelection,
  } = useValidator(inputFields, newWarehouse);
  const { renderField } = useFormBuilder(inputFields, newWarehouse, setNewWarehouse, errors);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const data = await getHierarchyStructures();
        setDepartments(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    setNewWarehouse((prev) => {
      const next = { ...prev, allDepartments: allDepartments ? 1 : 0 };
      if (allDepartments) {
        next.departments = [-1];
      } else if (Array.isArray(prev.departments) && prev.departments.length === 1 && prev.departments[0] === -1) {
        next.departments = [];
      }
      return next;
    });
  }, [allDepartments]);

  useEffect(() => {
    if (typeof newWarehouse.allDepartments !== 'undefined') {
      setAllDepartments(Boolean(newWarehouse.allDepartments));
    }
  }, [newWarehouse.allDepartments]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (warehouseList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [warehouseList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const getDepartmentLabel = (item) => {
    const selectedDepartmentIds = Array.isArray(item?.departments)
      ? item.departments.map((id) => Number(id))
      : [];

    if (selectedDepartmentIds.includes(-1)) {
      return 'All Departments';
    }

    const selectedDepartmentNames = departments
      .filter((department) => selectedDepartmentIds.includes(Number(department.id)))
      .map((department) => department.name);

    return selectedDepartmentNames.join(', ');
  };

  const handleEdit = (item) => {
    const allDeps = Array.isArray(item.departments) && item.departments.length === 1 && Number(item.departments[0]) === -1;
    setNewWarehouse({
      ...item,
      departments: Array.isArray(item.departments) ? item.departments.map((id) => Number(id)) : [-1],
      allDepartments: allDeps ? 1 : 0,
    });
    setAllDepartments(allDeps);
    setEditingId(item.id);
    setErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this warehouse?')) return;
    try {
      await deleteLocation(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchWarehouses();
    } catch (error) {
      console.error('Error deleting warehouse:', error);
    }
  };

  const handleSave = async () => {
    const baseValid = validate();
    const departmentsValid = validateDepartmentsSelection({
      allDepartments,
      departments: newWarehouse.departments,
    });
    if (!baseValid || !departmentsValid) return;
    try {
      const payload = {};
      for (const key in newWarehouse) {
        if (key === 'departments' || key === 'allDepartments') continue;
        if (newWarehouse[key] !== null && typeof newWarehouse[key] !== 'object') {
          payload[key] = newWarehouse[key];
        }
      }
      payload.departments = allDepartments
        ? [-1]
        : (Array.isArray(newWarehouse.departments) ? newWarehouse.departments.map((id) => Number(id)) : []);
      if (editingId) {
        await updateLocation(editingId, payload);
      } else {
        await createLocation(payload);
      }

      setShowModal(false);
      setEditingId(null);

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      setNewWarehouse({});
      setAllDepartments(true);
      await fetchWarehouses();
    } catch (error) {
      console.error('Error saving warehouse:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center border-0 pb-16">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('locations:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewWarehouse({});
              setAllDepartments(true);
              setErrors({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Warehouse
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-3 rounded-0 w-100" ref={tableRef}>
            <thead>
              <tr>
                <th className="rounded-0">Name</th>
                <th>Description</th>
                <th>Departments</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {warehouseList.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.description}</td>
                  <td className="text-wrap" style={{ whiteSpace: 'normal', minWidth: '220px' }}>
                    <span className="badge text-bg-primary text-wrap" style={{ whiteSpace: 'normal' }}>
                      {getDepartmentLabel(item)}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('locations:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('locations:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {warehouseList.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-2">
                    No Warehouses found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Warehouse' : 'New Warehouse'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">{inputFields.map((field) => renderField(field))}</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseListLayer;
